import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PayloadAction } from '@reduxjs/toolkit';

import { Icons } from '@icons/icons';

import { useQueryAssetOwners } from '@hooks/assets/useQueryAssetOwners';
import { useQueryAssetRentRequest } from '@hooks/assets/useQueryAssetRentRequest';
import { useQueryAssetRentRequestStatuses } from '@hooks/assets/useQueryAssetRentRequestStatuses';

import { assetRentOutgoingRequestsSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setPage,
	setPageSize,
	setSearchValue,
	setSort,
} from '@slices/assetsRentOutgoingRequests';

import { getAssetRentOutgoingRequests } from '@api/assetsRent';

import { isEmpty } from '@utils/common';

import { IPageSize } from '@interfaces/common/store';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITableSort, TTableFilter } from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { ASSETS_RENT_OUTGOING_REQUESTS } from '@constants/queryKeys';

import AssetRentRequests from '../AssetRentRequests';
import { getFilters } from './getFilters';
import getOutgoingRequestsColumns from './getOutgoingRequestsColumns';

export const pageTitle = 'Outgoing Requests';

const AssetRentOutgoingRequests = () => {
	const dispatch = useAppDispatch();

	const [selectedAssetOwnerName, setSelectedAssetOwnerName] = useState<TSelectOption | null>(null);
	const [selectedRequestStatus, setSelectedRequestStatus] = useState<TSelectOption | null>(null);
	const [organisations, setOrganisations] = useState<TSelectOption[]>([]);

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
	} = useAppSelector(assetRentOutgoingRequestsSelector);

	const {
		requestsStatuses,
		isLoadingRequestsStatuses,
		isFetchingRequestsStatuses,
	} = useQueryAssetRentRequestStatuses();

	const handleSetAssetOwnerName = (name: TSelectOption): void => {
		setSelectedAssetOwnerName(name);
	};

	const handleSetSelectedRequestStatus = (status: TSelectOption): void => {
		setSelectedRequestStatus(status);
	};

	const filters: Array<TTableFilter> = useMemo(() => getFilters({
		selectedAssetOwnerName,
		selectedRequestStatus,
		setSelectedAssetOwnerName: handleSetAssetOwnerName,
		setSelectedRequestStatus: handleSetSelectedRequestStatus,
		requestsStatuses,
		organisations,
	}), [organisations, requestsStatuses, selectedAssetOwnerName, selectedRequestStatus]);

	const { assetOwners } = useQueryAssetOwners(false);

	const {
		requests,
		countOfRecords,
		isLoading: isUseRequestLoading,
	} = useQueryAssetRentRequest({
		filters,
		getRequestFunction: getAssetRentOutgoingRequests,
		isAO: false,
		page,
		pageSize,
		queryKey: ASSETS_RENT_OUTGOING_REQUESTS,
		searchValue,
		sortBy: [{ id: sortFieldName, desc: !!sortDirection }],
	});

	useEffect(() => {
		if (isEmpty(assetOwners)) return;

		setOrganisations(assetOwners);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assetOwners?.length]);

	const handleSort = useCallback((sortBy: ISortBy[]) => {
		if (isEmpty(sortBy)) return;

		dispatch(setSort({
			sortFieldName: sortBy[0].id.split('.')[0],
			sortDirection: sortBy[0].desc,
		}));
	}, [dispatch]);

	const handleSetPage = useCallback((page: number): void => {
		dispatch(setPage({ page }));
	}, [dispatch]);

	const handleSetPageSize = (pageSize: number): PayloadAction<IPageSize> => dispatch(setPageSize({ pageSize }));

	const handleChangeSearchValue = useCallback((searchValue: string) => {
		handleSetPage(defaultMeta.page);
		dispatch(setSearchValue({ searchValue }));
	}, [dispatch, handleSetPage]);

	const sort: ITableSort = useMemo(() => {
		return {
			initialState: [{
				id: 'createdAt',
				desc: false,
			}],
			handleSort,
		};
	}, [handleSort]);

	return (
		<AssetRentRequests
			countOfRecords={countOfRecords}
			filters={filters}
			getColumns={getOutgoingRequestsColumns}
			handleSetPage={handleSetPage}
			handleSetPageSize={handleSetPageSize}
			isLoading={isUseRequestLoading || isLoadingRequestsStatuses || isFetchingRequestsStatuses}
			page={page}
			pageSize={pageSize}
			pageTitle={pageTitle}
			pageTitleIcon={Icons.outgoingRequestLight()}
			requests={requests}
			searchValue={searchValue}
			setSearchValue={handleChangeSearchValue}
			sort={sort}
		/>
	);
};

export default AssetRentOutgoingRequests;
